/**
 * Typdefinitioner för OdometerReport
 * Mätarställningsrapport per datum
 */

import i18n from '@/i18n/config';

export interface Organization {
  id: string;
  name: string;
  children?: Organization[];
  depth?: number;
  parentId?: string | null;
}

export interface Vehicle {
  id: number;
  registrationNumber: string;
  brand?: string;
  model?: string;
}

export interface OdometerEntry {
  vehicleId: number;
  registrationNumber: string;
  brand: string | null;
  model: string | null;
  organizationId: string;
  organizationName: string;
  odometerKm: number | null;
  odometerSource: 'oem' | 'calculated' | 'initial' | null;
  lastTripDate: string | null;
}

export interface ReportSummary {
  totalVehicles: number;
  vehiclesWithOdometer: number;
  vehiclesWithOem: number;
  vehiclesMissingOdometer: number;
}

// Helper function to find organization in tree
export function findOrgInTree(
  orgs: Organization[],
  id: string
): Organization | null {
  for (const org of orgs) {
    if (org.id === id) return org;
    if (org.children) {
      const found = findOrgInTree(org.children, id);
      if (found) return found;
    }
  }
  return null;
}

// Generate CSV content from entries
export function generateCsvContent(
  entries: OdometerEntry[],
  summary: ReportSummary | null,
  selectedDate: string
): string {
  const headers = [
    i18n.t('copy.reports.registrationNumber'),
    i18n.t('copy.reports.brand'),
    i18n.t('copy.reports.model'),
    i18n.t('copy.reports.organization'),
    i18n.t('copy.reports.odometerKm'),
    i18n.t('copy.reports.source'),
    i18n.t('copy.reports.latestTrip'),
  ];

  const rows = entries.map((e) => [
    e.registrationNumber,
    e.brand || '',
    e.model || '',
    e.organizationName,
    e.odometerKm !== null ? e.odometerKm.toString() : '',
    e.odometerSource === 'oem'
      ? 'OEM'
      : e.odometerSource === 'calculated'
      ? i18n.t('copy.reports.sourceCalculated')
      : e.odometerSource === 'initial'
      ? i18n.t('copy.reports.sourceInitial')
      : '',
    e.lastTripDate || '',
  ]);

  return [
    i18n.t('copy.reports.odometerReportTitle', { date: selectedDate }),
    '',
    headers.join(';'),
    ...rows.map((row) => row.join(';')),
    '',
    i18n.t('copy.reports.totalVehiclesLine', { count: summary?.totalVehicles || 0 }),
    i18n.t('copy.reports.withOdometerLine', { count: summary?.vehiclesWithOdometer || 0 }),
    i18n.t('copy.reports.withOemLine', { count: summary?.vehiclesWithOem || 0 }),
  ].join('\n');
}

// Download CSV file
export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob(['\ufeff' + content], {
    type: 'text/csv;charset=utf-8;',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
